// First-touch marketing attribution. Captured on every route (see
// AttributionCapture in App.js), persisted in localStorage, and attached to
// the user at signup via addReferral. UTM conventions live in
// JobHarvestAdvertising/ContentSystem/Analytics/channel-links-registry.md.

const ATTRIBUTION_KEY = "jh_attribution";
const CREATOR_CODE_KEY = "jh_creator_code";
const CREATOR_CODE_PATTERN = /^[A-Z0-9]{3,20}$/;

// Same rule as the server: trim, drop a leading @, uppercase. Returns "" when
// the result is not a 3-20 character A-Z/0-9 code.
export function normalizeCreatorCode(raw) {
  if (typeof raw !== "string") return "";
  const code = raw.trim().replace(/^@/, "").toUpperCase();
  return CREATOR_CODE_PATTERN.test(code) ? code : "";
}

export function getPendingCreatorCode() {
  try {
    return normalizeCreatorCode(localStorage.getItem(CREATOR_CODE_KEY)) || "";
  } catch (error) {
    return "";
  }
}

// Last click wins, as affiliate links usually work: the creator whose link the
// visitor followed most recently is the one whose code the page showed them.
// Returns true when a valid code was stored.
export function setPendingCreatorCode(raw) {
  const code = normalizeCreatorCode(raw);
  if (!code) return false;
  try {
    localStorage.setItem(CREATOR_CODE_KEY, code);
    return true;
  } catch (error) {
    return false;
  }
}

export function clearPendingCreatorCode() {
  try {
    localStorage.removeItem(CREATOR_CODE_KEY);
  } catch (error) {
    /* storage unavailable */
  }
}

export function captureAttribution(search, referrer, landingPath) {
  const params = new URLSearchParams(search);

  // Referral invites keep their existing localStorage keys so the current
  // signup flow (processReferral -> addReferral) keeps working unchanged.
  const referrerId = params.get("invite");
  if (referrerId) {
    localStorage.setItem("referrerId", referrerId);
    localStorage.setItem("referralSource", referrer || "direct");
  }

  // Creator links (?creator=ALEX) work on any route, independent of UTMs.
  setPendingCreatorCode(params.get("creator"));

  // First touch wins: never overwrite the source that originally brought
  // this browser here.
  if (localStorage.getItem(ATTRIBUTION_KEY)) return;

  const utmSource = params.get("utm_source");
  const utmMedium = params.get("utm_medium");
  const utmCampaign = params.get("utm_campaign");
  const utmContent = params.get("utm_content");

  if (!utmSource && !utmMedium && !utmCampaign && !referrer && !referrerId) {
    return;
  }

  localStorage.setItem(
    ATTRIBUTION_KEY,
    JSON.stringify({
      utmSource,
      utmMedium,
      utmCampaign,
      utmContent,
      referrer: referrer || "direct",
      landingPath,
      firstSeenAt: new Date().toISOString(),
    })
  );
}

export function getAttribution() {
  try {
    return JSON.parse(localStorage.getItem(ATTRIBUTION_KEY)) || {};
  } catch (error) {
    return {};
  }
}

const APP_STORE_BASE_URL =
  "https://apps.apple.com/us/app/jobharvest/id6762484907";
const ASC_PROVIDER_TOKEN = process.env.REACT_APP_ASC_PROVIDER_TOKEN;

// App Store Connect campaign link. `ct` mirrors utm_source so ASC's
// Campaigns report lines up with PostHog by name; `pt` is the provider
// token from App Store Connect > Analytics > Acquisition > Campaigns.
export function buildAppStoreUrl(campaignToken) {
  if (!ASC_PROVIDER_TOKEN) return APP_STORE_BASE_URL;
  const ct = campaignToken || getAttribution().utmSource || "web-mobileapp";
  return `${APP_STORE_BASE_URL}?pt=${ASC_PROVIDER_TOKEN}&ct=${encodeURIComponent(
    ct
  )}&mt=8`;
}
